import { useEffect, useMemo, useState } from "react";
import { api, downloadCsv } from "../lib/api";
import { Link } from "react-router-dom";
import { ClientLink } from "./AdminClient";
import { toast } from "sonner";
import { Search, Download, MailPlus, Users, AlertCircle, XCircle, Clock, CheckCircle2, ChevronDown, ChevronRight, MapPin, Pencil, CalendarClock } from "lucide-react";
import { ClientInfoForm } from "../components/ClientInfoForm";

const STATUS_LABEL = {
  active: "Actif",
  gift_active: "Cadeau en cours",
  prepaid_active: "Formule 3/6/12 mois",
  gift_ended: "Cadeau terminé",
  gift_pending: "Cadeau en attente",
  cancel_scheduled: "Résiliation programmée",
  canceled: "Annulé",
  payment_failed: "Paiement échoué",
  pending: "En attente de paiement",
};

const STATUS_COLOR = {
  active: "text-[#5C7A5C]",
  gift_active: "text-[#5C7A5C]",
  prepaid_active: "text-[#5C7A5C]",
  gift_ended: "text-[#7C7064]",
  gift_pending: "text-[#B87333]",
  cancel_scheduled: "text-[#B87333]",
  canceled: "text-[#7C7064]",
  payment_failed: "text-[#A86962]",
  pending: "text-[#B87333]",
};

const MAILING_LABEL = { to_prepare: "À préparer", prepared: "Préparé", sent: "Envoyé" };
const MAILING_NEXT = { to_prepare: "prepared", prepared: "sent", sent: "to_prepare" };
const MAILING_COLOR = {
  to_prepare: "bg-white border-[#E8DCC8] text-[#7C7064]",
  prepared: "bg-[#F5F0E6] border-[#A3876A] text-[#A3876A]",
  sent: "bg-[#DCE8DC] border-[#7A9A7A] text-[#5C7A5C]",
};

// 6 mois glissants à partir du mois d'envoi en cours (1ʳᵉ enveloppe : novembre 2026) — les mois passés disparaissent
function getMonths() {
  const out = [];
  const now = new Date();
  let y = now.getFullYear(), m = now.getMonth() + 1;
  if (y * 100 + m < 202611) { y = 2026; m = 11; }
  for (let i = 0; i < 6; i++) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    const label = new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "short" });
    out.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1), year: y });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return out;
}

function StatCard({ icon: Icon, label, value, color = "" }) {
  return (
    <div className="bg-[#F5F0E6] p-5 border border-[#E8DCC8]">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-2 ${color || "text-[#7C7064]"}`}>
        <Icon size={14}/> {label}
      </div>
      <p className="font-serif text-3xl text-[#2C2621]">{value}</p>
    </div>
  );
}

function SubscriberRow({ s, months, onMark }) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const mailings = s.mailings || {};
  async function toggleMailing(month) {
    const cur = mailings[month]?.status || "to_prepare";
    const next = MAILING_NEXT[cur];
    setBusy(month);
    try {
      await api.post(`/admin/mailclub/subscribers/${encodeURIComponent(s.email)}/mailing/${month}`,
        { status: next });
      onMark?.(s.email, month, next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  }
  const addr = s.address || {};
  return (
    <>
      <tr className="border-t border-[#E8DCC8] hover:bg-[#FDFBF7]">
        <td className="p-3">
          <button data-testid={`mc-toggle-${s.email}`} onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1 text-[#4A4036] hover:text-[#A3876A]">
            {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <span className="font-medium">{s.first_name} {s.last_name}</span>
          </button>
        </td>
        <td className="p-3 text-xs text-[#7C7064]"><ClientLink email={s.email}/></td>
        <td className={`p-3 text-xs ${STATUS_COLOR[s.status] || ""}`}>
          {STATUS_LABEL[s.status] || s.status}
          {s.deferred && (
            <span className="ml-1 text-[10px] bg-[#FBE9E4] border border-[#C35A3E] text-[#C35A3E] px-1.5 py-0.5" data-testid={`mc-deferred-badge-${s.email}`}>
              ⏳ dès {s.coverage_start_label}
            </span>
          )}
          {s.is_gift && (
            <span className="ml-1 text-[10px] bg-[#F5F0E6] border border-[#A3876A] text-[#A3876A] px-1.5 py-0.5">
              🎁 {s.gift_months_remaining ?? 0}/{s.gift_months_total ?? 0}
            </span>
          )}
        </td>
        <td className="p-3 text-xs text-[#7C7064]" data-testid={`mc-paid-${s.email}`}>
          {(s.last_payment_at || "").slice(0, 10) || "—"}
          {(s.payment_history || []).length > 0 && <span className="ml-1 text-[#2C2621]">· {s.payment_history[s.payment_history.length - 1].amount} €</span>}
          {s.is_gift && s.gift_paid_amount != null && <span className="ml-1 text-[#2C2621]">· cadeau {s.gift_paid_amount} €</span>}
        </td>
        <td className="p-3 text-xs text-[#7C7064]">{(s.current_period_end || "").slice(0, 10) || "—"}</td>
        {months.map(m => {
          const st = mailings[m.key]?.status || "to_prepare";
          return (
            <td key={m.key} className="p-2 text-center">
              <button
                data-testid={`mc-mailing-${s.email}-${m.key}`}
                onClick={() => toggleMailing(m.key)}
                disabled={busy === m.key}
                title={`${m.label}: ${MAILING_LABEL[st]} — clic pour cycler`}
                className={`px-2 py-1 text-[10px] border w-full ${MAILING_COLOR[st]} disabled:opacity-50`}
              >
                {MAILING_LABEL[st]}
              </button>
            </td>
          );
        })}
        <td className="p-2 text-right">
          <button data-testid={`mc-edit-${s.email}`} onClick={() => { setEditing(e => !e); setOpen(true); }}
            title="Modifier prénom, nom, adresse" className="p-1.5 text-[#A3876A] hover:text-[#C35A3E] hover:bg-[#F5F0E6] transition-colors">
            <Pencil size={14}/>
          </button>
        </td>
      </tr>
      {open && editing && (
        <tr className="bg-[#FDFBF7]">
          <td colSpan={6 + months.length} className="p-4 pl-10">
            <ClientInfoForm client={{ email: s.email, name: `${s.first_name || ""} ${s.last_name || ""}`.trim(), address: s.address, mailclub: [s] }}
              onDone={() => { setEditing(false); onMark?.(); }} onCancel={() => setEditing(false)}/>
          </td>
        </tr>
      )}
      {open && (
        <tr className="bg-[#FDFBF7]">
          <td colSpan={6 + months.length} className="p-4 text-xs text-[#4A4036]">
            <div className="grid sm:grid-cols-2 gap-6 pl-6">
              <div>
                <p className="uppercase tracking-wider text-[#7C7064] mb-2 flex items-center gap-1">
                  <MapPin size={12}/> Adresse postale
                </p>
                <p>{s.first_name} {s.last_name}</p>
                <p>{addr.line1}</p>
                {addr.line2 && <p>{addr.line2}</p>}
                <p>{addr.postal_code} {addr.city}</p>
                <p>{addr.country}</p>
              </div>
              <div>
                <p className="uppercase tracking-wider text-[#7C7064] mb-2">
                  {s.is_gift ? "Cadeau" : "Historique paiements"}
                </p>
                {s.is_gift ? (
                  <>
                    <p>Offert par <strong>{s.gift_buyer_name}</strong> ({s.gift_buyer_email})</p>
                    <p className="mt-1">Durée : <strong>{s.gift_months_total} mois</strong> — restant : <strong>{s.gift_months_remaining ?? 0}</strong></p>
                    {s.gift_dedication && <p className="mt-3 italic bg-[#F5F0E6] p-2 border-l-2 border-[#A3876A]">« {s.gift_dedication} »</p>}
                  </>
                ) : (
                  (s.payment_history || []).length === 0
                    ? <p className="italic text-[#7C7064]">Aucun paiement enregistré</p>
                    : (s.payment_history || []).slice(0, 6).map((p, i) => (
                      <p key={i}>{(p.paid_at || "").slice(0, 10)} — {p.amount}€</p>
                    ))
                )}
                <p className="mt-3 text-[#7C7064]">
                  Inscrite le {(s.created_at || "").slice(0, 10)}{!s.is_gift && ` · Consentement mkt : ${s.marketing_consent ? "oui" : "non"}`}
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminMailClub() {
  const [data, setData] = useState({ stats: {}, subscribers: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const months = useMemo(() => getMonths(), []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/admin/mailclub/subscribers${filter !== "all" ? `?status=${filter}` : ""}`);
      setData(r.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter]);
  const [syncing, setSyncing] = useState(false);
  async function syncPayments() {
    setSyncing(true);
    try {
      const r = await api.post("/admin/mailclub/sync-payments");
      toast.success(`${r.data.checked} abonnée(s) vérifiée(s) · ${r.data.payments_added} paiement(s) récupéré(s)`);
      if (r.data.errors?.length) toast.error(r.data.errors[0]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de synchronisation"); } finally { setSyncing(false); }
  }

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return data.subscribers;
    return data.subscribers.filter(s =>
      (s.first_name || "").toLowerCase().includes(t) ||
      (s.last_name || "").toLowerCase().includes(t) ||
      (s.email || "").toLowerCase().includes(t));
  }, [q, data.subscribers]);

  async function exportCsv() {
    try {
      await downloadCsv("/admin/mailclub/export.csv", "mailclub-adresses.csv");
      toast.success("Export téléchargé");
    } catch (e) { toast.error(`Export impossible (${e.message})`); }
  }

  async function notify(templateId, label) {
    if (!window.confirm(`Envoyer "${label}" à tous les abonnés actifs ?`)) return;
    try {
      const r = await api.post("/admin/mailclub/notify", { template_id: templateId });
      toast.success(`Envoyé à ${r.data.sent} abonné(s)${r.data.failed ? ` (${r.data.failed} échec)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  async function bulkMark(month) {
    const status = window.prompt(`Marquer TOUS les abonnés actifs comme… pour ${month} ?\n\n"prepared" ou "sent"`);
    if (!status || !["prepared", "sent"].includes(status)) return;
    if (!window.confirm(`Confirmer "${status}" pour ${month} ?`)) return;
    try {
      const r = await api.post("/admin/mailclub/mailings/bulk", { month, status });
      toast.success(`${r.data.affected} abonné(s) mis à jour`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  const s = data.stats || {};
  return (
    <div data-testid="admin-mailclub-page" className="max-w-full">
      <p className="font-hand text-xl text-[#A3876A] mb-2">Le rituel du mois</p>
      <h1 className="font-serif text-4xl mb-10">Club de Lettres</h1>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
        <StatCard icon={Users} label="Actifs (abo)" value={loading ? "…" : (s.active ?? 0)}/>
        <StatCard icon={Users} label="🎁 Cadeaux en cours" value={loading ? "…" : (s.gift_active ?? 0)}/>
        <StatCard icon={CalendarClock} label="Envois différés" value={loading ? "…" : (s.deferred ?? 0)} color="text-[#C35A3E]"/>
        <StatCard icon={Users} label="Nouveaux (30j)" value={loading ? "…" : (s.new_30d ?? 0)}/>
        <StatCard icon={Clock} label="Résil. programmée" value={loading ? "…" : (s.cancel_scheduled ?? 0)} color="text-[#B87333]"/>
        <StatCard icon={AlertCircle} label="Paiements échoués" value={loading ? "…" : (s.payment_failed ?? 0)} color="text-[#A86962]"/>
        <StatCard icon={XCircle} label="Annulés / Cadeaux finis" value={loading ? "…" : ((s.canceled ?? 0) + (s.gift_ended ?? 0))} color="text-[#7C7064]"/>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4" data-testid="mc-tabs">
        <button data-testid="mc-tab-active" onClick={() => setFilter("all")}
          className={`px-4 py-2 text-sm border ${filter !== "deferred" ? "bg-[#A3876A] text-white border-[#A3876A]" : "bg-white border-[#E8DCC8] text-[#4A4036] hover:bg-[#F5F0E6]"}`}>
          File active
        </button>
        <button data-testid="mc-tab-deferred" onClick={() => setFilter("deferred")}
          className={`px-4 py-2 text-sm border flex items-center gap-1.5 ${filter === "deferred" ? "bg-[#C35A3E] text-white border-[#C35A3E]" : "bg-white border-[#E8DCC8] text-[#4A4036] hover:bg-[#F5F0E6]"}`}>
          <CalendarClock size={14}/> Envois différés · en attente {!loading && s.deferred ? <span className="ml-1 text-[11px] opacity-80">({s.deferred})</span> : null}
        </button>
      </div>

      {filter === "deferred" ? (
        <DeferredTable rows={filtered} loading={loading} q={q} setQ={setQ}/>
      ) : (<>
      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button data-testid="mc-sync-payments" onClick={syncPayments} disabled={syncing}
          className="order-last ml-auto text-xs border border-[#E8DCC8] px-3 py-2 bg-white hover:bg-[#F5F0E6] text-[#4A4036]">
          {syncing ? "Synchronisation…" : "Synchroniser les paiements Stripe"}
        </button>
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C7064]"/>
          <input data-testid="mc-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Rechercher par nom ou email…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-[#E8DCC8] text-sm outline-none focus:border-[#A3876A]"/>
        </div>
        <select data-testid="mc-filter" value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-[#E8DCC8] text-sm outline-none">
          <option value="all">Toutes</option>
          <option value="active">Actives</option>
          <option value="gifts">🎁 Cadeaux (tous)</option>
          <option value="gift_active">Cadeaux en cours</option>
          <option value="gift_ended">Cadeaux terminés</option>
          <option value="new">Nouvelles (30j)</option>
          <option value="cancel_scheduled">Résiliation programmée</option>
          <option value="canceled">Annulées</option>
          <option value="payment_failed">Paiement échoué</option>
          <option value="pending">En attente</option>
        </select>
        <Link data-testid="mc-labels-link" to="/admin/etiquettes?scope=mailclub"
          className="flex items-center gap-1.5 text-sm border border-[#E8DCC8] bg-white px-3 py-2 hover:bg-[#F5F0E6]">
          <MapPin size={13}/> Étiquettes du mois
        </Link>
        <button data-testid="mc-export" onClick={exportCsv}
          className="flex items-center gap-1.5 text-sm border border-[#E8DCC8] bg-white px-3 py-2 hover:bg-[#F5F0E6]">
          <Download size={13}/> Export CSV
        </button>
        <button onClick={() => notify("mailclub_before_mailing", "Email pré-envoi")}
          className="flex items-center gap-1.5 text-sm bg-[#A3876A] text-white px-3 py-2 hover:bg-[#8f7457]">
          <MailPlus size={13}/> Prévenir (avant envoi)
        </button>
        <button onClick={() => notify("mailclub_after_mailing", "Email post-envoi")}
          className="flex items-center gap-1.5 text-sm border border-[#A3876A] text-[#A3876A] px-3 py-2 hover:bg-[#F5F0E6]">
          <MailPlus size={13}/> Prévenir (après envoi)
        </button>
      </div>

      {/* Grille des envois mensuels */}
      <div className="bg-white border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#F5F0E6] text-left text-xs uppercase tracking-wider text-[#7C7064]">
            <tr>
              <th className="p-3">Nom</th>
              <th className="p-3">Email</th>
              <th className="p-3">Statut</th>
              <th className="p-3">Dernier paiement</th>
              <th className="p-3">Prochain</th>
              {months.map(m => (
                <th key={m.key} className="p-2 text-center">
                  <button onClick={() => bulkMark(m.key)} title="Cliquer pour marquer TOUS les actifs"
                    className="hover:text-[#A3876A]">
                    {m.label} {String(m.year).slice(-2)}
                  </button>
                </th>
              ))}
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6 + months.length} className="p-6 text-center text-[#7C7064]">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6 + months.length} className="p-6 text-center text-[#7C7064]">
                {q ? "Aucun abonné ne correspond à ta recherche." : "Aucun abonné pour le moment."}
              </td></tr>
            )}
            {!loading && filtered.map(sub => (
              <SubscriberRow key={sub.email} s={sub} months={months} onMark={load}/>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#7C7064] mt-4">
        Clique sur un nom pour voir l'adresse. Clique sur une case mois pour cycler : À préparer → Préparé → Envoyé. Clique sur l'en-tête d'un mois pour appliquer un statut à tous les actifs.
      </p>
      </>)}
    </div>
  );
}

function DeferredTable({ rows, loading, q, setQ }) {
  return (
    <div data-testid="mc-deferred-panel">
      <p className="text-sm text-[#4A4036] mb-4 max-w-2xl">
        Cadeaux et formules déjà <strong>payés</strong> dont la première enveloppe est prévue pour un mois ultérieur. Ils passent automatiquement dans la file active le mois de leur première enveloppe — rien à faire d'ici là.
      </p>
      <div className="relative max-w-md mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C7064]"/>
        <input data-testid="mc-deferred-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher par nom ou email…"
          className="w-full pl-9 pr-3 py-2 bg-white border border-[#E8DCC8] text-sm outline-none focus:border-[#A3876A]"/>
      </div>
      <div className="bg-white border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#F5F0E6] text-left text-xs uppercase tracking-wider text-[#7C7064]">
            <tr>
              <th className="p-3">Destinataire</th>
              <th className="p-3">Offert par</th>
              <th className="p-3">1ʳᵉ enveloppe</th>
              <th className="p-3">Dans</th>
              <th className="p-3">Durée</th>
              <th className="p-3">Formule</th>
              <th className="p-3">Payé</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-[#7C7064]">Chargement…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#7C7064]" data-testid="mc-deferred-empty">
                {q ? "Aucun envoi différé ne correspond à ta recherche." : "Aucun envoi différé en attente."}
              </td></tr>
            )}
            {!loading && rows.map(s => (
              <tr key={s.email} className="border-t border-[#E8DCC8] hover:bg-[#FDFBF7]" data-testid={`mc-deferred-row-${s.email}`}>
                <td className="p-3">
                  <div className="font-medium text-[#4A4036]">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-[#7C7064]"><ClientLink email={s.email}/></div>
                  {s.gift_surprise && <span className="inline-block mt-1 text-[10px] bg-[#FBE9E4] border border-[#C35A3E] text-[#C35A3E] px-1.5 py-0.5">Surprise · non prévenu·e</span>}
                </td>
                <td className="p-3 text-xs text-[#7C7064]">
                  {s.is_gift ? <>{s.gift_buyer_name || "—"}<br/>{s.gift_buyer_email}</> : <span className="italic">elle-même</span>}
                </td>
                <td className="p-3 font-medium text-[#C35A3E]">{s.coverage_start_label}</td>
                <td className="p-3 text-xs text-[#4A4036]">{s.months_until_start} mois</td>
                <td className="p-3 text-xs text-[#4A4036]">{s.gift_months_total || s.months_total} mois</td>
                <td className="p-3 text-xs text-[#7C7064]">{s.is_gift ? "Cadeau" : "Formule"} · {(s.zone || "fr") === "fr" ? "France" : "International"}</td>
                <td className="p-3 text-xs text-[#4A4036]">{s.gift_paid_amount ?? s.paid_amount ?? "—"} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
